import { createContext, useContext, useMemo, type ReactNode } from 'react'
import type { IconPack } from './types'
import type { IconPackId } from '../theme/types'
import { heroiconsPack } from './packs/heroicons'
import { lucidePack } from './packs/lucide'
import { featherPack } from './packs/feather'
import { tablerPack } from './packs/tabler'

interface IconContextValue {
  pack: IconPack
  fallbackPack: IconPack | null
  strokeWidth: number
}

const PACKS: Record<IconPackId, IconPack> = {
  hi2: heroiconsPack,
  lu: lucidePack,
  fi: featherPack,
  tb: tablerPack,
}

const IconContext = createContext<IconContextValue>({
  pack: heroiconsPack,
  fallbackPack: lucidePack,
  strokeWidth: 1.5,
})

interface IconProviderProps {
  children: ReactNode
  packId: IconPackId
  fallbackPackId?: IconPackId
  strokeWidth?: number
}

export function IconProvider({ children, packId, fallbackPackId, strokeWidth = 1.5 }: IconProviderProps) {
  const value = useMemo<IconContextValue>(() => ({
    pack: PACKS[packId] ?? heroiconsPack,
    fallbackPack: fallbackPackId ? (PACKS[fallbackPackId] ?? null) : null,
    strokeWidth,
  }), [packId, fallbackPackId, strokeWidth])

  return <IconContext.Provider value={value}>{children}</IconContext.Provider>
}

export function useIconContext(): IconContextValue {
  return useContext(IconContext)
}
